import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  Refresh,
  Search,
  Warning,
  CheckCircle,
  SwapHoriz,
  Close,
  CalendarToday,
  AccountBalance,
  CreditCard,
  AttachMoney,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import CreditRiskService from '../../services/creditRiskService';
import UserService from '../../services/userService';
import InterestRateService from '../../services/interestRateService';
import UserSpecificInterestRateService from '../../services/userSpecificInterestRateService';
import useRoleColors from '../../hooks/useRoleColors';

const RiskManagement = ({ user }) => {
  const theme = useTheme();
  const {
    accent,
    accentLight,
    accentDark,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    softBgStrong,
    contrast,
    outline,
  } = useRoleColors();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [closeDialog, setCloseDialog] = useState({ open: false, risk: null });
  const [convertDialog, setConvertDialog] = useState({ open: false, risk: null });
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [activeNakdiRate, setActiveNakdiRate] = useState(null);
  const [userSpecificRate, setUserSpecificRate] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded payment guarantee rows

  // Fetch users for filter dropdown only
  useEffect(() => {
    const fetchUsers = async () => {
      const result = await UserService.getAllUsers(1, 1000);
      if (result.success) {
        setUsers(result.data || []);
      }
    };
    fetchUsers();
  }, []);

  // Fetch active nakdi interest rate
  useEffect(() => {
    const fetchActiveRate = async () => {
      try {
        // InterestRateManagement sayfasındaki gibi getAllRates kullan ve client-side filtrele
        const result = await InterestRateService.getAllRates();
        
        if (result.success && result.data && Array.isArray(result.data)) {
          // Aktif nakdi faiz oranını bul
          // Sadece expirationDate kontrolü yap (effectiveDate kontrolü kaldırıldı)
          const activeRate = result.data.find(r => {
            if (!r.isActive || !r.rate || r.rate <= 0) {
              return false;
            }
            
            const now = new Date();
            const expiration = r.expirationDate ? new Date(r.expirationDate) : null;
            
            // Tarih kontrolü: expirationDate bugünden sonra veya yok (sınırsız)
            const isNotExpired = !expiration || expiration >= now;
            
            return isNotExpired;
          });
          
          if (activeRate) {
            // Küçük harf (rate) veya büyük harf (Rate) kontrol et
            const rate = activeRate.rate || activeRate.Rate;
            if (rate != null && rate !== undefined && rate > 0) {
              setActiveNakdiRate(rate);
            } else {
              setActiveNakdiRate(null);
            }
          } else {
            setActiveNakdiRate(null);
          }
        } else {
          setActiveNakdiRate(null);
        }
      } catch (error) {
        console.error('Error fetching nakdi interest rate:', error);
        setActiveNakdiRate(null);
      }
    };
    fetchActiveRate();
  }, []);

  // Fetch risks
  const fetchRisks = async () => {
    setLoading(true);
    setError('');

    try {
      let result;
      if (selectedUserId) {
        result = await CreditRiskService.getRisksByUserId(selectedUserId);
      } else {
        // Get all risks by getting all users and their risks
        const allUsersResult = await UserService.getAllUsers(1, 1000);
        if (allUsersResult.success && allUsersResult.data) {
          const allRisks = [];
          for (const u of allUsersResult.data) {
            const riskResult = await CreditRiskService.getRisksByUserId(u.userId);
            if (riskResult.success && riskResult.data) {
              allRisks.push(...riskResult.data);
            }
          }
          result = { success: true, data: allRisks };
        } else {
          result = { success: false, data: [] };
        }
      }
      
      if (result.success) {
        setRisks(result.data || []);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Credits could not be loaded');
      console.error(err);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    fetchRisks();
  }, [selectedUserId]);

  // Get user name - use backend data if available, otherwise fallback to users list
  const getUserName = (risk) => {
    // First try to use userName from backend (risk.userName)
    if (risk.userName) {
      return risk.userName;
    }
    // Fallback to users list if backend didn't provide userName
    const user = users.find(u => u.userId === risk.userId);
    if (user) {
      return `${user.name || ''} ${user.surname || ''}`.trim() || user.email || `User #${risk.userId}`;
    }
    return `User #${risk.userId}`;
  };

  // Filter risks based on search term
  const filteredRisks = risks.filter(risk => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const userId = risk.userId?.toString() || '';
    const userName = getUserName(risk).toLowerCase();
    const riskTypeText = getRiskTypeText(risk.riskType).toLowerCase();
    const statusText = getStatusText(risk.status, risk.isOverdue).toLowerCase();
    
    return userId.includes(searchTerm) ||
           userName.includes(searchLower) ||
           riskTypeText.includes(searchLower) ||
           statusText.includes(searchLower);
  });

  // Handle close risk
  const handleCloseRisk = async () => {
    if (!closeDialog.risk) return;
    
    const result = await CreditRiskService.closeRisk(closeDialog.risk.id);
    if (result.success) {
      toast.success('Credits closed');
      setCloseDialog({ open: false, risk: null });
      fetchRisks();
    }
  };

  // Handle convert risk
  const handleConvertRisk = async () => {
    if (!convertDialog.risk) return;
    
    // Otomatik olarak aktif nakdi faiz oranını kullan (null gönder, backend otomatik alacak)
    const result = await CreditRiskService.convertRisk(convertDialog.risk.id, null);
    if (result.success) {
      toast.success('Credits converted to overdraft');
      setConvertDialog({ open: false, risk: null });
      fetchRisks();
    }
  };

  // Get status color
  const getStatusColor = (status, isOverdue = false) => {
    if (isOverdue) {
      return 'error'; // Overdue (red)
    }
    switch (status) {
      case 1: return 'success'; // Active (green)
      case 2: return 'default'; // Closed
      case 3: return 'info'; // Converted (gray)
      default: return 'default';
    }
  };

  // Get status text
  const getStatusText = (status, isOverdue = false) => {
    if (isOverdue) {
      return 'Overdue';
    }
    switch (status) {
      case 1: return 'Active';
      case 2: return 'Closed';
      case 3: return 'Converted';
      default: return 'Unknown';
    }
  };

  // Get risk type text
  // RiskType enum mapping:
  // - GayriNakdi (1) = Payment Guarantee (Non-cash credit risk)
  // - Nakdi (2) = Overdraft (Cash credit risk / Debt current account)
  const getRiskTypeText = (type) => {
    switch (type) {
      case 1: return 'Payment Guarantee'; // GayriNakdi
      case 2: return 'Overdraft'; // Nakdi
      default: return 'Unknown';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  // Sort risks: first by userId, then by user name
  const sortedRisks = [...filteredRisks].sort((a, b) => {
    // First sort by userId
    if (a.userId !== b.userId) {
      return a.userId - b.userId;
    }
    // If same userId, sort by user name
    const nameA = getUserName(a).toLowerCase();
    const nameB = getUserName(b).toLowerCase();
    return nameA.localeCompare(nameB, 'tr');
  });

  // Get risk stats
  const getRiskStats = () => {
    const active = filteredRisks.filter(r => r.status === 1).length;
    const closed = filteredRisks.filter(r => r.status === 2).length;
    const total = filteredRisks.length;
    const totalAmount = filteredRisks.reduce((sum, r) => sum + (r.riskAmount || 0), 0);
    return { active, closed, total, totalAmount };
  };

  // Group risks by creditId: Payment guarantees as main rows, overdrafts as child rows
  const groupedRisks = React.useMemo(() => {
    const paymentGuarantees = filteredRisks.filter(r => r.riskType === 1); // Payment Guarantee risks
    const overdrafts = filteredRisks.filter(r => r.riskType === 2); // Overdraft risks
    
    // Group overdrafts by creditId
    const overdraftsByCreditId = {};
    overdrafts.forEach(overdraft => {
      const creditId = overdraft.creditId || overdraft.credit?.id;
      if (creditId) {
        if (!overdraftsByCreditId[creditId]) {
          overdraftsByCreditId[creditId] = [];
        }
        overdraftsByCreditId[creditId].push(overdraft);
      }
    });
    
    // Sort payment guarantees
    const sortedPaymentGuarantees = [...paymentGuarantees].sort((a, b) => {
      if (a.userId !== b.userId) {
        return a.userId - b.userId;
      }
      const nameA = getUserName(a).toLowerCase();
      const nameB = getUserName(b).toLowerCase();
      return nameA.localeCompare(nameB, 'tr');
    });
    
    return { paymentGuarantees: sortedPaymentGuarantees, overdraftsByCreditId };
  }, [filteredRisks, users]);

  // Toggle expand/collapse for a payment guarantee row
  const toggleExpand = (riskId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(riskId)) {
        newSet.delete(riskId);
      } else {
        newSet.add(riskId);
      }
      return newSet;
    });
  };

  const stats = getRiskStats();

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <CreditCard sx={{ fontSize: 32 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Loan Management
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage payment guarantees and overdrafts
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={fetchRisks}
                  disabled={loading}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>

            {/* Stats */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))'
                },
                gap: 2,
                mb: 4
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    p: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 3, 
                      backgroundColor: softBg,
                      mr: 2
                    }}>
                      <CreditCard sx={{ color: accent, fontSize: 28 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Total Credit
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: accent, mb: 1 }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total credits
                  </Typography>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    p: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 3, 
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      mr: 2
                    }}>
                      <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Active Credit
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active credits
                  </Typography>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    p: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 3, 
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      mr: 2
                    }}>
                      <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Closed
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                    {stats.closed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Closed credits
                  </Typography>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    p: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 3, 
                      backgroundColor: softBg,
                      mr: 2
                    }}>
                      <AttachMoney sx={{ color: accent, fontSize: 28 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Total Amount
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: accent, mb: 1 }}>
                    {formatCurrency(stats.totalAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total amount
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Filters */}
            <Box sx={{ mb: 3, p: 3, backgroundColor: 'grey.50', borderRadius: 3 }}>
              <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth
                  sx={{
                    width: '100%',
                    minWidth: '200px',
                    '& .MuiInputBase-root': {
                      width: '100%',
                      minWidth: '200px'
                    },
                    '& .MuiSelect-select': {
                      width: '100%',
                      minWidth: '200px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      width: '100%'
                    }
                  }}
                >
                  <InputLabel>Filter by User</InputLabel>
                  <Select
                    value={selectedUserId || ''}
                    label="Filter by User"
                    onChange={(e) => setSelectedUserId(e.target.value || null)}
                    sx={{
                      width: '100%',
                      minWidth: '200px',
                      '& .MuiSelect-select': {
                        width: '100%',
                        minWidth: '200px'
                      }
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.userId} value={u.userId}>
                        {u.name} {u.surname} ({u.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            </Box>

            {/* Risks Table */}
            <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', width: 40, minWidth: 40 }}></TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>User ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>User Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Credit Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Reference Number</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                      <CircularProgress size={40} sx={{ color: accent }} />
                      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Loading credits...</Typography>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                      <Alert severity="error" sx={{ display: 'inline-flex' }}>{error}</Alert>
                    </TableCell>
                  </TableRow>
                ) : groupedRisks.paymentGuarantees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                      <Box sx={{ display: 'inline-flex', p: 3, borderRadius: 3, backgroundColor: 'grey.100', mb: 2 }}><Warning sx={{ fontSize: 48, color: 'grey.400' }} /></Box>
                      <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>No Payment Guarantees Found</Typography>
                      <Typography variant="body2" color="text.secondary">No payment guarantees match your search criteria.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupedRisks.paymentGuarantees.map((risk) => {
                    const creditId = risk.creditId || risk.credit?.id;
                    const relatedOverdrafts = creditId ? (groupedRisks.overdraftsByCreditId[creditId] || []) : [];
                    const hasOverdrafts = relatedOverdrafts.length > 0;
                    const isExpanded = expandedRows.has(risk.id);
                    
                    return (
                      <React.Fragment key={risk.id}>
                        <TableRow 
                          hover 
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: softBg, 
                              borderLeftColor: risk.status === 1 ? 'success.main' : risk.status === 2 ? 'grey.400' : 'grey.300' 
                            }, 
                            borderLeft: '4px solid transparent' 
                          }}
                        >
                          <TableCell sx={{ width: 40, minWidth: 40, padding: '8px !important' }}>
                            {hasOverdrafts && (
                              <IconButton
                                size="small"
                                onClick={() => toggleExpand(risk.id)}
                                sx={{
                                  color: 'primary.main',
                                  padding: '4px',
                                  '&:hover': {
                                    backgroundColor: 'primary.lighter',
                                  }
                                }}
                              >
                                {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              #{risk.userId}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                            {getUserName(risk)}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getRiskTypeText(risk.riskType)} 
                              size="small"
                              color={risk.riskType === 1 ? 'primary' : 'secondary'}
                              sx={{ 
                                fontWeight: 600, 
                                borderRadius: 3,
                                fontSize: '0.8rem',
                                height: '24px'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                            {risk.paymentGuaranteeNumber || '-'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: accent, fontSize: '0.8rem' }}>
                            {formatCurrency(risk.riskAmount)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Chip 
                              label={getStatusText(risk.status, risk.isOverdue)} 
                              size="small"
                              color={getStatusColor(risk.status, risk.isOverdue)}
                              sx={{ fontWeight: 600, borderRadius: 3, color: 'white' }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                            {formatDate(risk.dueDate)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                               {risk.status === 1 && risk.riskType === 1 && risk.isOverdue && (
                                <Tooltip title="Convert to Overdraft">
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      // Fetch user-specific rate for this risk's user
                                      if (risk.userId) {
                                        const userSpecificResult = await UserSpecificInterestRateService.getCurrentRateByUserId(risk.userId);
                                        if (userSpecificResult.success && userSpecificResult.data && userSpecificResult.data.rate) {
                                          setUserSpecificRate(userSpecificResult.data.rate);
                                        } else {
                                          setUserSpecificRate(null);
                                        }
                                      } else {
                                        setUserSpecificRate(null);
                                      }
                                      setConvertDialog({ open: true, risk });
                                      // Aktif nakdi faiz oranını al (dialog açıldığında güncelle)
                                      try {
                                        // InterestRateManagement sayfasındaki gibi getAllRates kullan
                                        const result = await InterestRateService.getAllRates();
                                        
                                        if (result.success && result.data && Array.isArray(result.data)) {
                                          // Aktif ve geçerli tarih aralığında olan oranı bul
                                          // Sadece expirationDate kontrolü yap (effectiveDate kontrolü kaldırıldı)
                                          const activeRate = result.data.find(r => 
                                            r.isActive && 
                                            (!r.expirationDate || new Date(r.expirationDate) >= new Date())
                                          );
                                          
                                          if (activeRate) {
                                            const rate = activeRate.rate || activeRate.Rate;
                                            if (rate != null && rate !== undefined && rate > 0) {
                                              setActiveNakdiRate(rate);
                                            } else {
                                              setActiveNakdiRate(null);
                                            }
                                          } else {
                                            setActiveNakdiRate(null);
                                          }
                                        } else {
                                          setActiveNakdiRate(null);
                                        }
                                      } catch (error) {
                                        console.error('Error fetching nakdi rate:', error);
                                        setActiveNakdiRate(null);
                                      }
                                    }}
                                    sx={{
                                      color: 'secondary.main',
                                      backgroundColor: 'secondary.lighter',
                                      '&:hover': {
                                        backgroundColor: 'secondary.main',
                                        color: 'white',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <SwapHoriz fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {risk.status === 1 && (
                                <Tooltip title="Close Credit">
                                  <IconButton
                                    size="small"
                                    onClick={() => setCloseDialog({ open: true, risk })}
                                    sx={{
                                      color: 'success.main',
                                      backgroundColor: 'success.lighter',
                                      '&:hover': {
                                        backgroundColor: 'success.main',
                                        color: 'white',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        {/* Expandable row for related overdrafts */}
                        {hasOverdrafts && (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ 
                                  p: 2, 
                                  backgroundColor: 'grey.100',
                                  borderRadius: 3,
                                  mt: 1,
                                  mb: 1
                                }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>User ID</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>User Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Credit Type</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Reference Number</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Amount</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'center' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Due Date</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'center' }}>Interest Rate</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'center' }}>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {relatedOverdrafts.map((overdraft) => (
                                        <TableRow key={overdraft.id} hover>
                                          <TableCell sx={{ fontSize: '0.75rem' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                              #{overdraft.userId}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ fontSize: '0.75rem' }}>
                                            {getUserName(overdraft)}
                                          </TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={getRiskTypeText(overdraft.riskType)} 
                                              size="small"
                                              color="secondary"
                                              sx={{ 
                                                fontWeight: 600, 
                                                borderRadius: 3, 
                                                fontSize: '0.75rem',
                                                height: '24px'
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.75rem' }}>
                                            {overdraft.overdraftNumber || '-'}
                                          </TableCell>
                                          <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: accent, fontSize: '0.75rem' }}>
                                            {formatCurrency(overdraft.riskAmount)}
                                          </TableCell>
                                          <TableCell sx={{ textAlign: 'center' }}>
                                            <Chip 
                                              label={getStatusText(overdraft.status, overdraft.isOverdue)} 
                                              size="small"
                                              color={getStatusColor(overdraft.status, overdraft.isOverdue)}
                                              sx={{ fontWeight: 600, borderRadius: 3, color: 'white', fontSize: '0.7rem' }}
                                            />
                                          </TableCell>
                                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            {formatDate(overdraft.dueDate)}
                                          </TableCell>
                                          <TableCell sx={{ textAlign: 'center', fontSize: '0.75rem' }}>
                                            {overdraft.interestRate ? `%${overdraft.interestRate}` : '-'}
                                          </TableCell>
                                          <TableCell sx={{ textAlign: 'center' }}>
                                            {overdraft.status === 1 && (
                                              <Tooltip title="Close Credit">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => setCloseDialog({ open: true, risk: overdraft })}
                                                  sx={{
                                                    color: 'success.main',
                                                    backgroundColor: 'success.lighter',
                                                    '&:hover': {
                                                      backgroundColor: 'success.main',
                                                      color: 'white',
                                                      transform: 'scale(1.1)'
                                                    },
                                                    transition: 'all 0.2s'
                                                  }}
                                                >
                                                  <CheckCircle fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {groupedRisks.paymentGuarantees.length > 0 && (
            <Box sx={{ p: 3, borderTop: 2, borderColor: 'divider', background: `linear-gradient(90deg, ${softBg} 0%, ${softBgHover} 100%)` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  Total Amount:
                </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: accent }}>
                  {formatCurrency(stats.totalAmount)}
                </Typography>
              </Box>
            </Box>
            )}
            </Box>
          </CardContent>
        </Card>

        {/* Close Risk Dialog */}
        <Dialog 
          open={closeDialog.open} 
          onClose={() => setCloseDialog({ open: false, risk: null })}
          PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}
        >
          <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
            <CheckCircle sx={{ fontSize: 24 }} />
            Close Credit
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {closeDialog.risk && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to <strong>close</strong> this credit? The limit will be returned when the credit is closed.
                  </Typography>
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Credit Amount:</strong> {formatCurrency(closeDialog.risk.riskAmount)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Credit Type:</strong> {getRiskTypeText(closeDialog.risk.riskType)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setCloseDialog({ open: false, risk: null })} 
              variant="outlined" 
              sx={{ borderRadius: 3 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCloseRisk} 
              color="success" 
              variant="contained"
              sx={{ 
                color: 'white', 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #00c853 0%, #00a726 100%)'
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Convert Risk Dialog */}
        <Dialog 
          open={convertDialog.open} 
          onClose={() => {
            setConvertDialog({ open: false, risk: null });
            setUserSpecificRate(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              background: accentGradient,
              color: contrast,
              py: 2.5,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwapHoriz sx={{ fontSize: 24 }} />
              <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                Convert Credit (Payment Guarantee → Overdraft)
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                setConvertDialog({ open: false, risk: null });
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <DialogContentText sx={{ mb: 3 }}>
              Are you sure you want to convert this payment guarantee credit to overdraft (debt current account)?
              <br />
              <strong>Note:</strong> Interest will be calculated for overdue days and added to the new overdraft risk amount.
              <br />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                <strong>About Commission Rate:</strong> Commission rates are currently defined in the Interest Rate Management page but are not used in risk conversion operations. Commission rates will be used in discount operations in the future.
              </Typography>
            </DialogContentText>
            {convertDialog.risk && (
              <Box sx={{ mt: 2 }}>
                <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Current Credit Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Credit Amount:</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {formatCurrency(convertDialog.risk.riskAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Due Date:</Typography>
                        <Typography variant="body1" fontWeight="600">
                          {convertDialog.risk.dueDate 
                            ? new Date(convertDialog.risk.dueDate).toLocaleDateString('en-US')
                            : 'Not Specified'}
                        </Typography>
                      </Grid>
                      {convertDialog.risk.dueDate && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Days Overdue: 
                            <strong style={{ color: theme.palette.warning.main }}>
                              {Math.max(0, Math.floor((new Date() - new Date(convertDialog.risk.dueDate)) / (1000 * 60 * 60 * 24)))} days
                            </strong>
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Interest Rate
                  </Typography>
                  {(() => {
                    // Use user-specific rate if available, otherwise use general rate
                    const rateToUse = userSpecificRate || activeNakdiRate;
                    const isUserSpecific = !!userSpecificRate;
                    
                    return rateToUse ? (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body1" fontWeight="bold">
                          {isUserSpecific ? 'User-Specific' : 'General'} Cash Interest Rate: %{rateToUse}
                        </Typography>
                        {isUserSpecific && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            This user has a specific rate defined. Using user-specific rate instead of general rate.
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          This rate will be used automatically and interest calculation will be performed in the credit conversion operation.
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                          Cash interest rate not found!
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Please first define a cash interest rate from the Interest Rate Management page.
                        </Typography>
                      </Alert>
                    );
                  })()}
                </Box>

                {convertDialog.risk.dueDate && (userSpecificRate || activeNakdiRate) && (userSpecificRate || activeNakdiRate) > 0 && (
                  <Card variant="outlined" sx={{ mt: 2, bgcolor: softBg, borderColor: outline }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Estimated Calculation
                      </Typography>
                      {(() => {
                        const daysOverdue = Math.max(0, Math.floor((new Date() - new Date(convertDialog.risk.dueDate)) / (1000 * 60 * 60 * 24)));
                        // Use user-specific rate if available, otherwise use general rate
                        const rate = userSpecificRate || activeNakdiRate || 0;
                        const interestAmount = convertDialog.risk.riskAmount * (rate / 100) * (daysOverdue / 365);
                        const newRiskAmount = convertDialog.risk.riskAmount + interestAmount;
                        const calculationBreakdown = `${formatCurrency(convertDialog.risk.riskAmount)} × (${rate}% / 100) × (${daysOverdue} / 365) = ${formatCurrency(interestAmount)}`;
                        
                        return (
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Days Overdue:</strong> {daysOverdue} days
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Interest Rate to Use:</strong> %{rate} (yearly)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Calculation Formula:</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, ml: 2, fontFamily: 'monospace', color: 'text.secondary' }}>
                              Principal × (Rate / 100) × (Days / 365)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, ml: 2, fontFamily: 'monospace', color: 'text.secondary' }}>
                              {calculationBreakdown}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
                              <strong>Estimated Interest Amount:</strong> {formatCurrency(interestAmount)}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>New Overdraft Amount:</strong> {formatCurrency(newRiskAmount)}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 0 }}>
            <Button 
              onClick={() => {
                setConvertDialog({ open: false, risk: null });
              }}
              variant="outlined"
              sx={{ borderRadius: 3 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConvertRisk} 
              color="primary" 
              variant="contained"
              startIcon={<SwapHoriz />}
              disabled={!(userSpecificRate || activeNakdiRate) || (userSpecificRate || activeNakdiRate) <= 0}
              sx={{ 
                color: contrast, 
                borderRadius: 3,
                background: accentGradient,
                '&:hover': {
                  background: accentGradientHover,
                },
                '&:disabled': {
                  background: softBgStrong,
                  color: 'text.disabled'
                }
              }}
            >
              Convert
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default RiskManagement;

